// Citizen Charter – LSGD (പൗരാവകാശ രേഖ) — member workspace.
// Fully schema-driven: sections, order, titles and colours come from the ACTIVE
// studio template (managed by Super Admin). Independent official LSGD identity —
// no KELTA branding. Autosaves section-wise; PDF is assembled client-side from
// dynamic pages + official fixed page sets (byte-preserved).
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SchemaSectionForm from "@/components/charter/SchemaSectionForm";
import BlocksEditor from "@/components/charter/SectionForms";
import PaletteEditor from "@/components/charter/PaletteEditor";
import PdfPreviewDialog from "@/components/charter/PdfPreviewDialog";
import FontSelector from "@/components/charter/FontSelector";
import { charterPdfArtifacts, charterFilename } from "@/lib/charterPdf";
import { loadFontPreview } from "@/lib/charterFonts";
import {
  ScrollText, User, Landmark, Users, BookOpen, LayoutGrid,
  Eye, Download, Lock, LockOpen, History, Loader2, FileDown, CheckCircle2, Palette,
} from "lucide-react";

const KIND_ICON = { cover: BookOpen, note: User, details: Landmark, record_table: Users, blocks: LayoutGrid, back_cover: ScrollText };

const STATUS_UI = {
  DRAFT: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  IN_PROGRESS: { label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  FINAL: { label: "Final", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
};

// legacy defaults so older charters keep their cover design fields
const LEGACY_DEFAULTS = {
  front_cover: { title_ml: "പൗരാവകാശ രേഖ", year: "2026", subtitle_en: "CITIZEN CHARTER" },
  president: { designation: "President" },
  design: { font_family: "Anek Malayalam" },
};

const isFilled = (pageDef, value) => {
  if (pageDef.kind === "record_table" || pageDef.kind === "blocks") return Array.isArray(value) && value.length > 0;
  if (!value || typeof value !== "object") return false;
  return (pageDef.config?.fields || []).some(f => (value[f.key] || "").toString().trim());
};

export default function CitizenCharterTab({ user }) {
  const [charter, setCharter] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [active, setActive] = useState(""); // page id or "design"
  const [saveState, setSaveState] = useState("saved");
  const [busyPreview, setBusyPreview] = useState(false);
  const [busyDownload, setBusyDownload] = useState(false);
  const [versions, setVersions] = useState([]);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const saveTimer = useRef(null);
  const pendingRef = useRef({ sections: {}, palette: null });
  const fixedSetCache = useRef({});

  // the active studio template decides the working year (2026, 2027, …)
  const year = template?.year || charter?.year || 2026;

  const loadVersions = (yr) => api.get("/charters/my/generated", { params: { year: yr || year } })
    .then(r => setVersions(r.data)).catch(() => {});

  useEffect(() => {
    (async () => {
      try {
        const t = await api.get("/charters/my/template");
        const tpl = t.data;
        setTemplate(tpl);
        const yr = tpl.year || 2026;
        const c = await api.get("/charters/my", { params: { year: yr } });
        const doc = c.data;
        doc.sections = { ...LEGACY_DEFAULTS, ...(doc.sections || {}) };
        doc.palette = doc.palette || tpl.palette;
        setCharter(doc);
        const firstPage = (tpl.pages || []).find(p => p.kind !== "fixed_set" && p.enabled !== false);
        setActive(firstPage ? firstPage.id : "design");
        loadFontPreview(doc.sections.design?.font_family || tpl.fonts?.family || "Anek Malayalam");
        loadVersions(yr);
      } catch (e) {
        setLoadError(e?.response?.data?.detail || "Could not load your Citizen Charter");
      }
    })();
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flushSave = async () => {
    clearTimeout(saveTimer.current);
    const { sections, palette } = pendingRef.current;
    if (!Object.keys(sections).length && !palette) return;
    pendingRef.current = { sections: {}, palette: null };
    setSaveState("saving");
    try {
      const body = {};
      if (Object.keys(sections).length) body.sections = sections;
      if (palette) body.palette = palette;
      const { data } = await api.put("/charters/my", body, { params: { year } });
      // adopt server state, but keep values re-edited while the request was in flight
      setCharter(c => {
        const merged = { ...LEGACY_DEFAULTS, ...(data.sections || {}) };
        for (const k of Object.keys(sections)) {
          if (pendingRef.current.sections[k] !== undefined) merged[k] = c.sections[k];
        }
        return {
          ...data,
          sections: merged,
          palette: pendingRef.current.palette ? c.palette : (data.palette || c.palette),
        };
      });
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      toast.error(e?.response?.data?.detail || "Autosave failed");
    }
  };

  const scheduleSave = () => {
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 1000);
  };

  const onChange = (key, value) => {
    setCharter(c => ({ ...c, sections: { ...c.sections, [key]: value } }));
    pendingRef.current.sections[key] = value;
    scheduleSave();
  };

  const onPalette = (palette) => {
    setCharter(c => ({ ...c, palette }));
    pendingRef.current.palette = palette;
    scheduleSave();
  };

  // fetch every fixed set referenced by the template (cached per session)
  const loadFixedSets = async () => {
    const out = {};
    const ids = (template?.pages || [])
      .filter(p => p.kind === "fixed_set" && p.enabled !== false)
      .map(p => p.config?.fixed_set_id)
      .filter(Boolean);
    await Promise.all(ids.map(async id => {
      if (!fixedSetCache.current[id]) {
        try {
          const { data } = await api.get(`/charters/fixed-sets/${id}/file`);
          fixedSetCache.current[id] = data.pdf_base64;
        } catch { fixedSetCache.current[id] = ""; }
      }
      out[id] = fixedSetCache.current[id];
    }));
    return out;
  };

  const preview = async () => {
    setBusyPreview(true);
    const tid = toast.loading("Assembling PDF — dynamic pages + official fixed pages…", { id: "cc-preview" });
    try {
      await flushSave();
      const { blobUrl } = await charterPdfArtifacts(charter, { template, fixedSets: await loadFixedSets() });
      setPreviewUrl(blobUrl); // in-app preview — no popup blockers
      toast.success("Preview ready", { id: tid });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Could not build the preview PDF", { id: tid });
    } finally { setBusyPreview(false); }
  };

  const download = async () => {
    setBusyDownload(true);
    const tid = toast.loading("Generating final Citizen Charter PDF…", { id: "cc-dl" });
    try {
      await flushSave();
      const { blobUrl, base64, pageCount } = await charterPdfArtifacts(charter, { template, fixedSets: await loadFixedSets() });
      const filename = charterFilename(charter);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      toast.success(`Downloaded ${filename} (${pageCount} pages)`, { id: tid });
      api.post("/charters/my/generated", { file_base64: base64, page_count: pageCount, filename }, { params: { year } })
        .then(loadVersions).catch(() => {});
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Could not generate the PDF", { id: tid });
    } finally { setBusyDownload(false); }
  };

  const finalize = async () => {
    setConfirmFinalize(false);
    try {
      const { data } = await api.post("/charters/my/finalize", null, { params: { year } });
      setCharter(c => ({ ...data, sections: { ...LEGACY_DEFAULTS, ...(data.sections || {}) }, palette: data.palette || c.palette }));
      toast.success("Charter marked FINAL — ready for printing");
    } catch (e) { toast.error(e?.response?.data?.detail || "Finalize failed"); }
  };

  const reopen = async () => {
    try {
      const { data } = await api.post("/charters/my/reopen", null, { params: { year } });
      setCharter(c => ({ ...data, sections: { ...LEGACY_DEFAULTS, ...(data.sections || {}) }, palette: data.palette || c.palette }));
      toast.success("Charter reopened for editing");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reopen failed"); }
  };

  const downloadVersion = async (v) => {
    const tid = toast.loading(`Fetching version ${v.version_number}…`, { id: `cc-ver-${v.id}` });
    try {
      const { data } = await api.get(`/charters/my/generated/${v.id}/download`);
      const a = document.createElement("a");
      a.href = data.file_base64;
      a.download = data.filename || `Citizen_Charter_v${v.version_number}.pdf`;
      a.click();
      toast.success("Version downloaded", { id: tid });
    } catch { toast.error("Could not download that version", { id: tid }); }
  };

  if (loadError) {
    return (
      <Card className="p-10 text-center" data-testid="cc-load-error">
        <ScrollText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h3 className="font-semibold">Citizen Charter unavailable</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{loadError}</p>
      </Card>
    );
  }
  if (!charter || !template) {
    return (
      <Card className="p-10 flex items-center justify-center gap-2 text-muted-foreground" data-testid="cc-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading Citizen Charter…
      </Card>
    );
  }

  const isFinal = charter.status === "FINAL";
  const st = STATUS_UI[charter.status] || STATUS_UI.DRAFT;
  const lb = charter.local_body || {};
  const dynamicPages = (template.pages || []).filter(p => p.kind !== "fixed_set" && p.enabled !== false);
  const filled = dynamicPages.filter(p => isFilled(p, charter.sections[p.section_key])).length;
  const activePage = dynamicPages.find(p => p.id === active);

  return (
    <div className="space-y-5" data-testid="citizen-charter-tab">
      {/* Independent LSGD-styled header — deliberately no KELTA branding */}
      <div className="rounded-xl overflow-hidden border border-emerald-900/20" data-testid="cc-header">
        <div className="bg-emerald-900 text-white px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/90 font-semibold">Local Self Government Department · തദ്ദേശ സ്വയംഭരണ വകുപ്പ്</div>
              <h2 className="text-xl sm:text-2xl font-black mt-0.5" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>
                പൗരാവകാശ രേഖ {year} <span className="font-sans text-sm font-semibold text-emerald-200/90">· Citizen Charter</span>
              </h2>
              <div className="text-xs text-emerald-100/80 mt-1">
                {lb.local_body_name} {lb.local_body_type ? `(${lb.local_body_type})` : ""} · {lb.district} · Template: {template.name}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] font-semibold rounded-full border px-2.5 py-1 ${st.cls} bg-white`} data-testid="cc-status-badge">{st.label}</span>
              <span className="text-[11px] text-emerald-100/80" data-testid="cc-save-state">
                {saveState === "saving" ? "Saving…" : saveState === "error" ? "Save failed" : "All changes saved"}
              </span>
            </div>
          </div>
          {/* progress: filled sections out of dynamic pages */}
          <div className="mt-3 flex items-center gap-3" data-testid="cc-progress">
            <div className="flex-1 h-1.5 rounded-full bg-emerald-800 overflow-hidden">
              <div className="h-full bg-amber-400 transition-all" style={{ width: `${dynamicPages.length ? (filled / dynamicPages.length) * 100 : 0}%` }} />
            </div>
            <span className="text-[11px] text-emerald-100/80">{filled}/{dynamicPages.length} sections filled</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" variant="secondary" className="bg-white text-emerald-900 hover:bg-emerald-50" onClick={preview} disabled={busyPreview} data-testid="cc-preview-btn">
              {busyPreview ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />} Preview PDF
            </Button>
            <Button size="sm" className="bg-amber-500 text-emerald-950 hover:bg-amber-400 font-semibold" onClick={download} disabled={busyDownload} data-testid="cc-download-btn">
              {busyDownload ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Download Final PDF
            </Button>
            {!isFinal ? (
              <Button size="sm" variant="outline" className="border-emerald-300/50 text-emerald-100 hover:bg-emerald-800 bg-transparent" onClick={() => setConfirmFinalize(true)} data-testid="cc-finalize-btn">
                <Lock className="w-4 h-4 mr-1.5" /> Mark as Final
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="border-emerald-300/50 text-emerald-100 hover:bg-emerald-800 bg-transparent" onClick={reopen} data-testid="cc-reopen-btn">
                <LockOpen className="w-4 h-4 mr-1.5" /> Reopen for Editing
              </Button>
            )}
          </div>
        </div>
        {isFinal && (
          <div className="bg-emerald-50 border-t border-emerald-200 px-5 py-2.5 text-xs text-emerald-800 flex items-center gap-2" data-testid="cc-final-banner">
            <CheckCircle2 className="w-4 h-4" /> This charter is <strong>FINAL</strong> (locked {String(charter.finalized_at || "").slice(0, 10)}). Editing is disabled — reopen to make changes.
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-5">
        {/* section navigator — titles come from the studio template */}
        <nav className="flex lg:flex-col gap-1.5 overflow-x-auto pb-1 lg:pb-0" data-testid="cc-section-nav">
          {dynamicPages.map(p => {
            const Icon = KIND_ICON[p.kind] || ScrollText;
            const isActive = active === p.id;
            const done = isFilled(p, charter.sections[p.section_key]);
            return (
              <button key={p.id} type="button" onClick={() => setActive(p.id)}
                className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors shrink-0 lg:shrink ${
                  isActive ? "border-emerald-700 bg-emerald-900 text-white" : "border-border bg-card hover:bg-muted/50"}`}
                data-testid={`cc-nav-${p.id}`}>
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-amber-300" : "text-emerald-800"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold leading-tight truncate" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>{p.title_ml || p.title_en}</span>
                  <span className={`block text-[10px] leading-tight ${isActive ? "text-emerald-200" : "text-muted-foreground"}`}>{p.title_en}</span>
                </span>
                {done && <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-amber-300" : "text-emerald-600"}`} />}
              </button>
            );
          })}
          <button type="button" onClick={() => setActive("design")}
            className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors shrink-0 lg:shrink ${
              active === "design" ? "border-emerald-700 bg-emerald-900 text-white" : "border-border bg-card hover:bg-muted/50"}`}
            data-testid="cc-nav-design">
            <Palette className={`w-4 h-4 shrink-0 ${active === "design" ? "text-amber-300" : "text-emerald-800"}`} />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold leading-tight truncate" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>നിറങ്ങളും അക്ഷരവും</span>
              <span className={`block text-[10px] leading-tight ${active === "design" ? "text-emerald-200" : "text-muted-foreground"}`}>Colours & Font</span>
            </span>
          </button>
        </nav>

        {/* active panel (locked when FINAL) */}
        <fieldset disabled={isFinal} className="min-w-0 disabled:opacity-70" data-testid="cc-form-area">
          {active === "design" ? (
            <Card className="p-5" data-testid="cc-page-design">
              <div className="mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>നിറങ്ങളും അക്ഷരവും</h3>
                <p className="text-xs text-muted-foreground">Colours & Font · applies to all dynamically generated pages — official fixed pages always stay exactly as uploaded</p>
              </div>
              <div className="mb-5 max-w-sm space-y-1.5">
                <span className="text-xs font-semibold">Malayalam Font</span>
                <FontSelector value={charter.sections.design?.font_family || template.fonts?.family || "Anek Malayalam"}
                  onChange={v => onChange("design", { ...(charter.sections.design || {}), font_family: v })} testid="cc-font" />
              </div>
              <PaletteEditor value={charter.palette || template.palette} onChange={onPalette} testid="cc-palette" />
            </Card>
          ) : activePage?.kind === "blocks" ? (
            <BlocksEditor pageDef={activePage} data={charter.sections[activePage.section_key]}
              onChange={v => onChange(activePage.section_key, v)} />
          ) : activePage ? (
            <SchemaSectionForm pageDef={activePage} data={charter.sections[activePage.section_key]}
              onChange={v => onChange(activePage.section_key, v)} user={user} />
          ) : null}
        </fieldset>
      </div>

      {/* generated versions history */}
      <Card className="p-5" data-testid="cc-versions">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-emerald-800" />
          <div className="font-bold text-sm">Generated PDF Versions</div>
          <span className="text-xs text-muted-foreground">a copy is stored every time you download</span>
        </div>
        {!versions.length ? (
          <p className="text-sm text-muted-foreground py-3 text-center" data-testid="cc-versions-empty">No generated PDFs yet — use “Download Final PDF”.</p>
        ) : (
          <ul className="divide-y divide-border">
            {versions.map(v => (
              <li key={v.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`cc-version-${v.version_number}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Version {v.version_number} · {v.page_count} pages</div>
                  <div className="text-xs text-muted-foreground truncate">{v.filename} · {new Date(v.generated_at).toLocaleString()}</div>
                </div>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadVersion(v)} data-testid={`cc-version-dl-${v.version_number}`}>
                  <FileDown className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <PdfPreviewDialog url={previewUrl} title={`പൗരാവകാശ രേഖ ${year} — Preview`} onClose={() => {
        if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ } }
        setPreviewUrl(null);
      }} />

      <AlertDialog open={confirmFinalize} onOpenChange={setConfirmFinalize}>
        <AlertDialogContent data-testid="cc-finalize-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Citizen Charter as FINAL?</AlertDialogTitle>
            <AlertDialogDescription>
              The charter will be locked for editing. You can still preview and download it, and you can reopen it later if corrections are needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cc-finalize-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={finalize} className="bg-emerald-700 hover:bg-emerald-800" data-testid="cc-finalize-confirm">Mark FINAL</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
